import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCSSVariables } from '../config/themeConfig';

// Create the theme context
const ThemeContext = createContext({
  isDarkMode: false,
  toggleTheme: () => {},
  theme: 'light'
});

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// ThemeProvider component
export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize theme from localStorage or default to light
  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        const isDark = savedTheme === 'dark';
        setIsDarkMode(isDark);
        updateCSSVariables(isDark);
      } else {
        // Default to light theme
        updateCSSVariables(false);
      }
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
      // Fallback to light theme
      updateCSSVariables(false);
    }
  }, []);

  // Update CSS custom properties when theme changes
  const updateCSSVariables = (isDark) => {
    try {
      const root = document.documentElement;
      const themeName = isDark ? 'dark' : 'light';
      const cssProps = getCSSVariables(themeName);
      
      // Set CSS custom properties using the centralized configuration
      Object.entries(cssProps).forEach(([property, value]) => {
        root.style.setProperty(property, value);
      });
      
      // Set data-theme attribute for CSS selectors
      root.setAttribute('data-theme', themeName);
    } catch (error) {
      console.warn('Failed to update CSS variables:', error);
    }
  };

  // Toggle theme function
  const toggleTheme = () => {
    const newIsDarkMode = !isDarkMode;
    setIsDarkMode(newIsDarkMode);
    
    // Update CSS variables
    updateCSSVariables(newIsDarkMode);
    
    // Save to localStorage
    try {
      localStorage.setItem('theme', newIsDarkMode ? 'dark' : 'light');
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  };

  const contextValue = {
    isDarkMode,
    toggleTheme,
    theme: isDarkMode ? 'dark' : 'light'
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;